import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants'
import {getLockString} from '../utils/get-lock-string'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {LockedEvent$key} from './__generated__/LockedEvent.graphql'
import type {LockedEventViewer$key} from './__generated__/LockedEventViewer.graphql'
import {TimelineRow} from './row/TimelineRow'
import {LockIcon} from '@primer/octicons-react'

type LockedEventProps = {
  queryRef: LockedEvent$key
  issueUrl: string
  issueRef?: LockedEventViewer$key
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
}

export const EventViewerPermission = graphql`
  fragment LockedEventViewer on Issue {
    repository {
      viewerCanPush
      owner {
        ...EventActor
      }
    }
  }
`

export function LockedEvent({issueRef, ...rest}: LockedEventProps): JSX.Element {
  if (!issueRef) {
    return <></>
  }

  return <LockedEventInternal {...rest} issueRef={issueRef} />
}

function LockedEventInternal({
  queryRef,
  issueUrl,
  issueRef,
  highlightedEventId,
  refAttribute,
}: Omit<LockedEventProps, 'issueRef'> & {issueRef: LockedEventViewer$key}): JSX.Element {
  const {actor, createdAt, lockReason, databaseId} = useFragment(
    graphql`
      fragment LockedEvent on LockedEvent {
        databaseId
        createdAt
        lockReason
        actor {
          ...EventActor
        }
      }
    `,
    queryRef,
  )
  const viewerData = useFragment(EventViewerPermission, issueRef)
  const lockReasonString = getLockString(lockReason)
  const highlighted = String(databaseId) === highlightedEventId

  const actorToUse = viewerData.repository.viewerCanPush ? actor : viewerData.repository.owner

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actorToUse}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      leadingIcon={LockIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.locked} `}
        {lockReason && <>{`${LABELS.timeline.as} ${lockReasonString} `}</>}
        {`${LABELS.timeline.limitedToCollaborators} `}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

try{ LockedEvent.displayName ||= 'LockedEvent' } catch {}
try{ LockedEventInternal.displayName ||= 'LockedEventInternal' } catch {}